import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { usePartnerBooking } from '../../context/PartnerBookingContext';
import { supabasePartner } from '../../lib/supabase';
import { fetchCruiseTimes, fetchBoardingPoint } from '../../lib/cruiseDetails';
import { formatCardDate, displayDeck, to12h, cruiseLabel } from '../../components/shared/helpers';
import { downloadTicket } from '../../utils/ticketPDF';

export default function PartnerConfirmation() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const {
    selectedDate, passengers, selectedCruise, paxDetails,
    bookingRef, tacRate, agentId, partnerName,
    setSelectedDate, setPassengers, setSelectedCruise, setPaxDetails,
    setAgreedTnC, setBookingRef, setAgentId, setAvailabilityCruises,
    setHoldId, setHoldExpiresAt, setHoldExpiredNotice,
    fading, go,
  } = usePartnerBooking();

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  // Set while starting a fresh booking: the reset below clears bookingRef/
  // selectedCruise, which the guard depends on. Without this flag, that clear
  // would trip the guard's redirect (and null-render the summary) during the
  // 250ms navigation fade. We render nothing until the navigation lands instead.
  const [restarting, setRestarting] = useState(false);

  // Boarding point via the shared cruiseDetails lookup (for the cruise date),
  // so it matches the rest of the site rather than being hardcoded here. (Same
  // source as the customer Confirmation screen.)
  const [boardingPoint, setBoardingPoint] = useState('');

  // Reporting time via the same lookup the ticket prints from, so the two can
  // never disagree. Seeded from the availability payload so the row is filled
  // on first paint, then refreshed in case the schedule changed since the
  // cruise was picked.
  const [reportingTime, setReportingTime] = useState(() => to12h(selectedCruise?.reportingTime));
  const cruiseCode = selectedCruise?.cruiseCode;

  useEffect(() => {
    if (!cruiseCode) return;
    let cancelled = false;
    (async () => {
      const times = await fetchCruiseTimes({ cruiseCode, date: selectedDate });
      const fresh = to12h(times?.reporting_time);
      if (!cancelled && fresh) setReportingTime(fresh);
    })();
    return () => { cancelled = true; };
  }, [cruiseCode, selectedDate]);

  // The agent this booking is attributed to — the ticket is sent to THIS agent's
  // WhatsApp (partner ticket routing). Fetched by agentId via the authenticated
  // partner client, so RLS scopes it to this partner's own agents.
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const boarding = await fetchBoardingPoint({ date: selectedDate });
      if (!cancelled && boarding?.boarding_point) setBoardingPoint(boarding.boarding_point);
    })();
    return () => { cancelled = true; };
  }, [selectedDate]);

  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabasePartner
        .from('partner_agents')
        .select('agent_name, agent_phone')
        .eq('id', agentId)
        .maybeSingle();
      if (!cancelled && data) setAgent(data);
    })();
    return () => { cancelled = true; };
  }, [agentId]);


  // Discounted partner pricing — consistent with PartnerCruiseSummary /
  // PartnerPayment. Display only; the actual charge was computed server-side.
  const rate = Number(tacRate) > 0 ? Number(tacRate) : 0;
  const publicAdult = selectedCruise?.priceAdult ?? 0;
  const publicChild = selectedCruise?.priceChild ?? 0;
  const priceAdult = rate > 0 ? Math.round(publicAdult * (1 - rate / 100)) : publicAdult;
  const priceChild = rate > 0 ? Math.round(publicChild * (1 - rate / 100)) : publicChild;
  const grandTotal = passengers.adults * priceAdult + passengers.children * priceChild;
  const publicGrandTotal = passengers.adults * publicAdult + passengers.children * publicChild;
  const savings = publicGrandTotal - grandTotal;

  // Passenger summary string (zero counts hidden)
  const paxSummary = [
    `${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'}`,
    passengers.children > 0 ? `${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'}` : null,
    passengers.infants > 0 ? `${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}` : null,
  ].filter(Boolean).join(' · ');


  const leadName = paxDetails[0]?.name || 'Guest';

  // Generate the boarding-pass PDF and trigger the browser save. downloadTicket
  // uses bookingRef as the preloadedReference, so booking.id can be null here.
  // (Same pattern as the customer Confirmation screen.)
  const handleDownload = async () => {
    if (downloading) return;
    setDownloadError('');
    setDownloading(true);
    try {
      const iso = selectedDate;
      const [y, m, d] = String(iso || '').split('-');
      const dateDdMmYyyy = (y && m && d) ? `${d}-${m}-${y}` : '';

      const booking = {
        id: null,                          // unavailable here; generator uses bookingRef instead
        booking_name: leadName,
        cruise_type: selectedCruise?.cruiseName || '',
        deck: selectedCruise?.deckLabel || '',
        date: dateDdMmYyyy,
        adults: passengers.adults,
        children: passengers.children,
        infants: passengers.infants,
        tour_leaders: 0,
      };

      await downloadTicket(booking, bookingRef);
    } catch (err) {
      console.error('Ticket download failed:', err);
      setDownloadError("Couldn't prepare the download. The ticket has also been sent to the agent's WhatsApp.");
    } finally {
      setDownloading(false);
    }
  };

  // Start a fresh partner booking — clear the partner booking state (so old
  // selections don't leak into the next booking) and return to the first step.
  // Only invoked on the finish action, never on mount, so the details above stay
  // available to display.
  const handleBookAnother = () => {
    setRestarting(true);
    setBookingRef('');
    setSelectedDate(null);
    setPassengers({ adults: 1, children: 0, infants: 0 });
    setSelectedCruise(null);
    setPaxDetails([]);
    setAgreedTnC(false);
    setAgentId(null);
    setAvailabilityCruises([]);
    setHoldId(null);
    setHoldExpiresAt(null);
    setHoldExpiredNotice(false);
    go('/partner/book/agent');
  };

  // Guards — wait for the auth check, then require an approved partner session
  // and a completed booking (a real reference). Without a booking there is
  // nothing to confirm, so fall back to the partner dashboard.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;
  // Mid-restart: state is being cleared and navigation is in flight — render
  // nothing rather than tripping the no-booking guard below.
  if (restarting) return null;
  if (!bookingRef || !selectedCruise) return <Navigate to="/partner" replace />;

  return (
    <div className={`w-full max-w-[600px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* SECTION 1 — Success moment */}
        <div className="flex flex-col items-center text-center">
          <div className="check-pop mb-3 md:mb-3">
            <svg viewBox="0 0 52 52" className="w-[72px] h-[72px] md:w-16 md:h-16" aria-hidden="true">
              <circle cx="26" cy="26" r="24" fill="rgba(201,168,76,0.08)" stroke="#C9A84C" strokeWidth="2" />
              <path className="check-draw" d="M16 27 L23 34 L37 19" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-1.5">
            Partner Booking
          </p>
          <h1 className="font-cormorant font-normal text-[2.4rem] md:text-[2.2rem] leading-[1.05] tracking-[-0.01em] text-white mb-1 md:mb-1.5">
            Booking Confirmed
          </h1>
          <p className="font-dm-sans text-sm md:text-[15px] font-medium text-[rgba(212,168,67,0.85)] mb-5 md:mb-5">
            See you on the Brahmaputra
          </p>
        </div>

        {/* SECTION 2 — Booking reference */}
        <div className="rounded-xl border border-[#C9A84C] bg-[#162840] px-5 py-5 md:py-3 mb-4 md:mb-4 text-center">
          <p className="font-dm-sans text-[13px] uppercase tracking-[0.22em] font-medium text-white/60 mb-2 md:mb-1">
            Booking Reference
          </p>
          <p className="font-cormorant text-[2rem] md:text-[2.1rem] leading-none text-[#C9A84C] tracking-[0.02em] mb-2.5 md:mb-1.5">
            {bookingRef}
          </p>
          <p className="font-dm-sans text-sm font-light text-white/55">
            Please keep this reference handy at boarding
          </p>
        </div>

        {/* SECTION 3 — Booking summary card (discounted partner total) */}
        <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 md:p-3 mb-4 md:mb-4">
          {[
            ['Date', formatCardDate(selectedDate)],
            ['Cruise', cruiseLabel(selectedCruise)],
            ['Deck', displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)],
            ['Time', selectedCruise.time],
            ['Reporting Time', reportingTime],
            ['Boarding', boardingPoint],
            ['Passengers', paxSummary],
          ].map(([label, value], i) => (
            <div key={label} className={`flex items-start justify-between gap-6 py-2.5 md:py-1.5 ${i > 0 ? 'border-t border-white/[0.06]' : ''}`}>
              <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">
                {label}
              </span>
              <span className={`font-dm-sans text-sm font-light text-white text-right leading-relaxed min-w-0 break-words ${label === 'Boarding' ? 'max-w-[62%]' : ''}`}>
                {value}
              </span>
            </div>
          ))}
          {rate > 0 && (
            <div className="flex items-center justify-between gap-4 py-2.5 md:py-1.5 border-t border-white/[0.06]">
              <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">
                Partner Rate
              </span>
              <span className="font-dm-sans text-[10px] font-bold uppercase tracking-[0.06em] text-[#0A1628] bg-[#C9A84C] rounded-full px-1.5 py-[3px] leading-none">
                &minus;{rate}%
              </span>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 pt-3 md:pt-2 mt-1 md:mt-0.5 border-t border-[rgba(212,168,67,0.25)]">
            <span className="font-dm-sans text-sm font-medium text-white">Total Paid</span>
            <span className="font-dm-sans text-lg font-bold text-[#C9A84C] tabular-nums">&#8377;{grandTotal.toLocaleString()}</span>
          </div>
          {rate > 0 && savings > 0 && (
            <p className="font-dm-sans text-[12px] font-light text-white/50 text-right mt-1.5">
              Saved &#8377;{savings.toLocaleString()} vs the public rate
            </p>
          )}
        </div>

        {/* SECTION 4 — Guest list */}
        {paxDetails.length > 0 && (
          <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 md:p-3 mb-4 md:mb-4">
            <p className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] mb-2.5">
              Guests
            </p>
            <ol className="flex flex-col gap-2">
              {paxDetails.map((p, i) => (
                <li key={i} className="flex items-center justify-between gap-4">
                  <span className="font-dm-sans text-sm font-light text-white leading-relaxed min-w-0 break-words">
                    {i + 1}. {p.name || '—'}
                  </span>
                  <span className="font-dm-sans text-[12px] uppercase tracking-[0.1em] font-medium text-white/45 shrink-0">
                    {p.type === 'adult' ? 'Adult' : p.type === 'child' ? 'Child' : 'Infant'}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* SECTION 5 — Agent attribution + ticket routing note */}
        <div className="rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-4 py-4 mb-4 md:mb-4">
          <div className="flex items-start justify-between gap-4">
            <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">
              Booked By
            </span>
            <div className="text-right min-w-0">
              <p className="font-dm-sans text-sm font-medium text-white leading-relaxed break-words">
                {agent?.agent_name || 'Agent'}
              </p>
              {agent?.agent_phone && (
                <p className="font-dm-sans text-[13px] font-light text-white/55 leading-relaxed break-words">
                  {agent.agent_phone}
                </p>
              )}
              {partnerName && (
                <p className="font-dm-sans text-[12px] font-light text-white/40 leading-relaxed break-words mt-0.5">
                  {partnerName}
                </p>
              )}
            </div>
          </div>
          <p className="font-dm-sans text-sm font-light text-white/70 leading-relaxed mt-3 pt-3 border-t border-white/[0.08]">
            The ticket has been sent to the agent&rsquo;s WhatsApp number. It may take a few minutes to arrive.
          </p>
        </div>

        {/* SECTION 6 — Download ticket */}
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="w-full py-[15px] md:py-0 md:h-12 md:flex md:items-center md:justify-center bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.98] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
        >
          {downloading ? 'Preparing…' : 'Download Ticket'}
        </button>
        {downloadError && (
          <p className="fade-in-soft font-dm-sans text-center text-sm text-[rgba(212,168,67,0.75)] mt-3 md:mt-2 leading-relaxed">
            {downloadError}
          </p>
        )}

        {/* SECTION 7 — Finish actions */}
        <div className="flex flex-col gap-2.5 mt-4">
          <button
            type="button"
            onClick={handleBookAnother}
            className="w-full py-[15px] bg-transparent border border-[rgba(212,168,67,0.5)] text-[#C9A84C] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.98] hover:border-[#C9A84C] hover:bg-[rgba(201,168,76,0.06)] transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628]"
          >
            Book Another Cruise
          </button>
          <button
            type="button"
            onClick={() => go('/partner')}
            className="block mx-auto mt-1 mb-2 font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
          >
            Back to dashboard
          </button>
        </div>

        {/* SECTION 8 — Closing line */}
        <p className="font-dm-sans text-center text-sm font-light text-white/55 leading-relaxed mt-4 md:mt-3 mb-2 max-w-[420px] mx-auto">
          Thank you for booking with Alfresco Grand.
        </p>

      </div>
  );
}
